/**
 * Product image zoom modal.
 * Opens from Dawn gallery clicks, with zoom, pan, pinch, and thumbnails.
 */
if (!customElements.get('product-zoom-modal')) {
  const ZOOM_LEVELS = [1, 1.5, 2, 3];
  const ANIM_DURATION = 200;

  class ProductZoomModal extends HTMLElement {
    connectedCallback() {
      this.triggerSelector = this.dataset.triggerSelector || '.product__modal-opener--image';
      this.imageSelector = this.dataset.imageSelector || '.product__modal-opener--image img';
      this.root = this.closest('product-info') || this.closest('.product') || document;
      this.images = [];
      this.imageEls = [];
      this.currentIdx = 0;
      this.zoomLevel = 1;
      this.zoomIdx = 0;
      this.isDragging = false;
      this.dragStart = { x: 0, y: 0 };
      this.imgOffset = { x: 0, y: 0 };
      this.lastOffset = { x: 0, y: 0 };
      this.lastPinchDist = null;
      this.pinchStartScale = 1;

      this.boundTriggerClick = this.onTriggerClick.bind(this);
      this.boundOnWheel = this.onWheel.bind(this);
      this.boundOnDragStart = this.onDragStart.bind(this);
      this.boundOnDragMove = this.onDragMove.bind(this);
      this.boundOnDragEnd = this.onDragEnd.bind(this);
      this.boundOnTouchStart = this.onTouchStart.bind(this);
      this.boundOnTouchMove = this.onTouchMove.bind(this);
      this.boundOnTouchEnd = this.onTouchEnd.bind(this);
      this.boundOnKeyDown = this.onKeyDown.bind(this);

      this.buildModal();
      this.bindTriggers();
    }

    disconnectedCallback() {
      this.unbindTriggers();
      this.unbindDocumentEvents();
      this.overlay?.remove();
    }

    bindTriggers() {
      this.triggers = Array.from(this.root.querySelectorAll(this.triggerSelector));
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', this.boundTriggerClick, true);
      });
    }

    unbindTriggers() {
      this.triggers?.forEach((trigger) => {
        trigger.removeEventListener('click', this.boundTriggerClick, true);
      });
    }

    unbindDocumentEvents() {
      document.removeEventListener('mousemove', this.boundOnDragMove);
      document.removeEventListener('mouseup', this.boundOnDragEnd);
      document.removeEventListener('keydown', this.boundOnKeyDown);
    }

    onTriggerClick(event) {
      event.preventDefault();
      event.stopImmediatePropagation();

      this.collectImages();
      if (!this.images.length) return;

      const trigger = event.currentTarget;
      const img = trigger.tagName === 'IMG' ? trigger : trigger.querySelector('img');
      const clickedIdx = img ? this.imageEls.indexOf(img) : -1;
      this.open(clickedIdx >= 0 ? clickedIdx : 0);
    }

    collectImages() {
      this.imageEls = Array.from(this.root.querySelectorAll(this.imageSelector)).filter(
        (node) => node.tagName === 'IMG'
      );
      this.images = this.imageEls.map((img) => ({
        src: img.dataset.zoomSrc || img.currentSrc || img.src,
        thumb: img.currentSrc || img.src,
        alt: img.alt,
      }));
    }

    buildModal() {
      this.overlay = el('div', 'pzm-overlay');
      this.overlay.setAttribute('role', 'dialog');
      this.overlay.setAttribute('aria-modal', 'true');
      this.overlay.setAttribute('aria-label', 'Image viewer');

      this.modal = el('div', 'pzm-modal');

      const header = el('div', 'pzm-header');
      this.zoomIndicator = el('span', 'pzm-zoom-indicator', '1×');
      this.zoomInBtn = iconBtn('pzm-btn pzm-zoom-in', svgPlus(), 'Zoom in');
      this.zoomOutBtn = iconBtn('pzm-btn pzm-zoom-out', svgMinus(), 'Zoom out');
      this.closeBtn = iconBtn('pzm-btn pzm-close', svgClose(), 'Close');
      header.append(this.zoomIndicator, this.zoomInBtn, this.zoomOutBtn, this.closeBtn);

      const stage = el('div', 'pzm-stage');
      this.imgEl = el('img', 'pzm-img');
      this.imgEl.setAttribute('draggable', 'false');
      stage.appendChild(this.imgEl);

      this.prevBtn = iconBtn('pzm-btn pzm-prev', svgChevron('left'), 'Previous image');
      this.nextBtn = iconBtn('pzm-btn pzm-next', svgChevron('right'), 'Next image');
      this.thumbsWrapper = el('div', 'pzm-thumbs');

      this.modal.append(header, stage, this.prevBtn, this.nextBtn, this.thumbsWrapper);
      this.overlay.appendChild(this.modal);
      document.body.appendChild(this.overlay);

      this.bindModalEvents();
    }

    bindModalEvents() {
      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', (event) => {
        if (event.target === this.overlay) this.close();
      });

      this.prevBtn.addEventListener('click', () => this.navigate(-1));
      this.nextBtn.addEventListener('click', () => this.navigate(1));
      this.zoomInBtn.addEventListener('click', () => this.changeZoomByStep(1));
      this.zoomOutBtn.addEventListener('click', () => this.changeZoomByStep(-1));

      this.imgEl.addEventListener('wheel', this.boundOnWheel, { passive: false });
      this.imgEl.addEventListener('mousedown', this.boundOnDragStart);
      document.addEventListener('mousemove', this.boundOnDragMove);
      document.addEventListener('mouseup', this.boundOnDragEnd);

      this.imgEl.addEventListener('touchstart', this.boundOnTouchStart, { passive: false });
      this.imgEl.addEventListener('touchmove', this.boundOnTouchMove, { passive: false });
      this.imgEl.addEventListener('touchend', this.boundOnTouchEnd);

      document.addEventListener('keydown', this.boundOnKeyDown);
    }

    onWheel(event) {
      event.preventDefault();
      if (event.deltaY < 0) this.changeZoomByStep(1);
      else this.changeZoomByStep(-1);
    }

    onDragStart(event) {
      if (this.zoomLevel <= 1) return;
      this.isDragging = true;
      this.dragStart = { x: event.clientX - this.lastOffset.x, y: event.clientY - this.lastOffset.y };
      this.imgEl.style.cursor = 'grabbing';
    }

    onDragMove(event) {
      if (!this.isDragging) return;
      this.imgOffset = {
        x: event.clientX - this.dragStart.x,
        y: event.clientY - this.dragStart.y,
      };
      this.applyTransform(false);
    }

    onDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.lastOffset = { ...this.imgOffset };
      this.imgEl.style.cursor = this.zoomLevel > 1 ? 'grab' : '';
    }

    onTouchStart(event) {
      if (event.touches.length === 2) {
        this.lastPinchDist = getPinchDist(event);
        this.pinchStartScale = this.zoomLevel;
        return;
      }
      if (this.zoomLevel > 1) {
        this.isDragging = true;
        this.dragStart = {
          x: event.touches[0].clientX - this.lastOffset.x,
          y: event.touches[0].clientY - this.lastOffset.y,
        };
      }
    }

    onTouchMove(event) {
      event.preventDefault();
      if (event.touches.length === 2) {
        const dist = getPinchDist(event);
        const ratio = dist / this.lastPinchDist;
        const newScale = clamp(this.pinchStartScale * ratio, ZOOM_LEVELS[0], ZOOM_LEVELS[ZOOM_LEVELS.length - 1]);
        this.setZoom(newScale, false);
        return;
      }
      if (!this.isDragging) return;
      this.imgOffset = {
        x: event.touches[0].clientX - this.dragStart.x,
        y: event.touches[0].clientY - this.dragStart.y,
      };
      this.applyTransform(false);
    }

    onTouchEnd(event) {
      if (event.touches.length < 2) this.lastPinchDist = null;
      if (this.isDragging) {
        this.isDragging = false;
        this.lastOffset = { ...this.imgOffset };
      }
    }

    onKeyDown(event) {
      if (!this.overlay?.classList.contains('is-open')) return;
      switch (event.key) {
        case 'ArrowLeft':
          this.navigate(-1);
          break;
        case 'ArrowRight':
          this.navigate(1);
          break;
        case '+':
        case '=':
          this.changeZoomByStep(1);
          break;
        case '-':
          this.changeZoomByStep(-1);
          break;
        case 'Escape':
          this.close();
          break;
      }
    }

    open(idx) {
      this.currentIdx = idx;
      this.resetZoom();
      this.renderThumbs();
      this.loadImage(idx);
      this.updateNavButtons();
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      this.closeBtn.focus();
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    navigate(dir) {
      if (this.images.length <= 1) return;
      this.currentIdx = (this.currentIdx + dir + this.images.length) % this.images.length;
      this.resetZoom();
      this.loadImage(this.currentIdx);
      this.updateThumbs();
      this.updateNavButtons();
    }

    updateNavButtons() {
      const disabled = this.images.length <= 1;
      this.prevBtn.disabled = disabled;
      this.nextBtn.disabled = disabled;
      this.prevBtn.hidden = disabled;
      this.nextBtn.hidden = disabled;
    }

    loadImage(idx) {
      const image = this.images[idx];
      if (!image) return;
      this.imgEl.style.opacity = '0';
      this.imgEl.src = image.src;
      this.imgEl.alt = image.alt || '';
      this.imgEl.onload = () => {
        this.imgEl.style.opacity = '1';
      };
    }

    changeZoomByStep(dir) {
      this.zoomIdx = clamp(this.zoomIdx + dir, 0, ZOOM_LEVELS.length - 1);
      this.setZoom(ZOOM_LEVELS[this.zoomIdx], true);
    }

    setZoom(scale, animate) {
      this.zoomLevel = scale;
      this.zoomIdx = ZOOM_LEVELS.reduce(
        (best, lvl, i) => (Math.abs(lvl - scale) < Math.abs(ZOOM_LEVELS[best] - scale) ? i : best),
        0
      );

      if (scale <= 1) {
        this.imgOffset = { x: 0, y: 0 };
        this.lastOffset = { x: 0, y: 0 };
      }

      this.applyTransform(animate);
      this.zoomIndicator.textContent = `${Math.round(scale * 100) / 100}×`;
      this.imgEl.style.cursor = scale > 1 ? 'grab' : '';
      this.zoomOutBtn.disabled = this.zoomIdx === 0;
      this.zoomInBtn.disabled = this.zoomIdx === ZOOM_LEVELS.length - 1;
    }

    applyTransform(animate) {
      this.imgEl.style.transition = animate ? `transform ${ANIM_DURATION}ms ease` : 'none';
      this.imgEl.style.transform = `translate(${this.imgOffset.x}px, ${this.imgOffset.y}px) scale(${this.zoomLevel})`;
    }

    resetZoom() {
      this.zoomIdx = 0;
      this.zoomLevel = 1;
      this.imgOffset = { x: 0, y: 0 };
      this.lastOffset = { x: 0, y: 0 };
      if (this.imgEl) {
        this.imgEl.style.transition = 'none';
        this.imgEl.style.transform = 'none';
      }
      if (this.zoomIndicator) this.zoomIndicator.textContent = '1×';
      if (this.zoomOutBtn) this.zoomOutBtn.disabled = true;
      if (this.zoomInBtn) this.zoomInBtn.disabled = false;
    }

    renderThumbs() {
      this.thumbsWrapper.innerHTML = '';
      this.thumbsWrapper.hidden = this.images.length <= 1;
      this.images.forEach((img, i) => {
        const thumb = el('button', 'pzm-thumb' + (i === this.currentIdx ? ' is-active' : ''));
        const thumbImg = el('img');
        thumbImg.src = img.thumb || img.src;
        thumbImg.alt = img.alt || '';
        thumb.appendChild(thumbImg);
        thumb.addEventListener('click', () => {
          this.currentIdx = i;
          this.resetZoom();
          this.loadImage(i);
          this.updateThumbs();
        });
        this.thumbsWrapper.appendChild(thumb);
      });
    }

    updateThumbs() {
      this.thumbsWrapper.querySelectorAll('.pzm-thumb').forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === this.currentIdx);
      });
      const active = this.thumbsWrapper.querySelector('.is-active');
      if (active) active.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
    }
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  }

  function iconBtn(cls, svgStr, label) {
    const button = el('button', `${cls} blured-bg`);
    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.innerHTML = svgStr;
    return button;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function getPinchDist(event) {
    const dx = event.touches[0].clientX - event.touches[1].clientX;
    const dy = event.touches[0].clientY - event.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function svgClose() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>`;
  }

  function svgPlus() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>`;
  }

  function svgMinus() {
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>`;
  }

  function svgChevron(dir) {
    const path = dir === 'left' ? 'M15 18l-6-6 6-6' : 'M9 18l6-6-6-6';
    return `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="${path}"/>
    </svg>`;
  }

  customElements.define('product-zoom-modal', ProductZoomModal);
}
